/**
 * Pads a number with leading zeros to ensure 2 digits
 */
function pad(num: number): string {
  return String(num).padStart(2, "0");
}

/**
 * Converts a Date object to ISO datetime format compatible with Valibot's v.isoDateTime()
 *
 * @param date - The Date object to convert (defaults to current date/time)
 * @returns ISO datetime string in format "yyyy-mm-ddThh:mm" (without milliseconds and timezone)
 *
 * @example
 * toISODateTime() // "2025-07-02T17:13"
 * toISODateTime(new Date('2025-12-25T10:30:00Z')) // "2025-12-25T10:30"
 */
export function toISODateTime(date: Date = new Date()): string {
  return date.toISOString().slice(0, 16);
}

/**
 * Converts a Date to HTML5 date input format (YYYY-MM-DD)
 *
 * @param date - The Date object to convert
 * @returns Date string in format "YYYY-MM-DD"
 *
 * @example
 * toDateInputValue(new Date('2024-03-15T14:30:00Z')) // "2024-03-15"
 */
export function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
}

/**
 * Converts a Date to HTML5 time input format (HH:MM)
 *
 * @param date - The Date object to convert
 * @returns Time string in format "HH:MM"
 *
 * @example
 * toTimeInputValue(new Date('2024-03-15T14:30:00Z')) // "14:30"
 */
export function toTimeInputValue(date: Date): string {
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${hours}:${minutes}`;
}

/**
 * Splits an ISO timestamp into separate date and time values for HTML5 inputs
 * Converts from UTC to local timezone
 *
 * @param isoString - ISO 8601 timestamp string
 * @returns Object with date (YYYY-MM-DD) and time (HH:MM) in local timezone
 *
 * @example
 * splitIsoToDateAndTime('2024-03-15T14:30:00.000Z')
 * // Returns: { date: "2024-03-15", time: "16:30" } (if local timezone is UTC+2)
 */
export function splitIsoToDateAndTime(isoString: string): { date: string; time: string } {
  const dateTime = new Date(isoString);
  return {
    date: toDateInputValue(dateTime),
    time: toTimeInputValue(dateTime),
  };
}

/**
 * Combines date and time strings into a Date object
 * Creates date in local timezone (not UTC)
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param timeStr - Time string in HH:MM format (optional)
 * @param options - Configuration options
 * @param options.endOfDay - If true and no time provided, sets to 23:59:59.999
 * @returns Date object in local timezone
 *
 * @example
 * combineDateAndTime('2024-03-15', '14:30')
 * // Returns: Date object for 2024-03-15 14:30:00 in local timezone
 *
 * combineDateAndTime('2024-03-15', undefined, { endOfDay: true })
 * // Returns: Date object for 2024-03-15 23:59:59.999
 *
 * combineDateAndTime('2024-03-15')
 * // Returns: Date object for 2024-03-15 00:00:00
 */
export function combineDateAndTime(dateStr: string, timeStr?: string, options?: { endOfDay?: boolean }): Date {
  const [year, month, day] = dateStr.split("-").map(Number);

  if (options?.endOfDay && !timeStr) {
    return new Date(year!, month! - 1, day!, 23, 59, 59, 999);
  }

  const [hours, minutes] = timeStr ? timeStr.split(":").map(Number) : [0, 0];

  return new Date(year!, month! - 1, day!, hours!, minutes!, 0, 0);
}
